import type { Direction } from "../core/types";
import type { SceneSessionRecord } from "../scene-training/SceneSessionStore";
import type { SceneBlockResult, SceneTrainingMode } from "../scene-training/types";

const DIRECTION_LABELS: Readonly<Record<Direction, string>> = {
  north: "前方", east: "右方", south: "后方", west: "左方",
};

export interface SceneStartRequest {
  readonly participantId: string;
  readonly sessionId: string;
  readonly mode: SceneTrainingMode;
}

export class SceneTrainingHud {
  readonly canvas: HTMLCanvasElement;
  private readonly root: HTMLElement;
  private readonly modal: HTMLElement;
  private readonly stage: HTMLElement;
  private readonly progress: HTMLElement;
  private readonly time: HTMLElement;
  private readonly performance: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly toast: HTMLElement;
  private readonly vignette: HTMLElement;
  private toastTimer: number | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    root.innerHTML = `
      <div class="training-shell">
        <canvas id="render-canvas" aria-label="听觉视觉场景训练"></canvas>
        <div class="vignette" aria-hidden="true"></div>
        <header class="status-bar">
          <span id="stage-label">准备</span><span id="progress-label">四场景 · 自主最终确认</span>
          <span id="time-label">有效时间 00:00</span><span id="performance-label">性能 -- FPS · L0</span>
        </header>
        <div id="focus-hint" class="focus-hint">开始后注视一张场景图片</div>
        <div id="feedback-toast" class="feedback-toast" role="status" aria-live="polite"></div>
        <nav class="action-strip" aria-label="桌面训练操作">
          <button id="preview-action" type="button"><kbd>Space</kbd> 试听脚步</button>
          <button id="primary-action" type="button"><kbd>E</kbd> 选择 / 最终确认</button>
          <button id="continue-action" type="button"><kbd>Q</kbd> 继续下一组</button>
          <button id="cancel-action" type="button"><kbd>C</kbd> 取消选中</button><span><kbd>Esc</kbd> 暂停</span>
        </nav>
        <section id="modal" class="modal" aria-live="polite"></section>
      </div>`;
    this.canvas = this.require("#render-canvas", HTMLCanvasElement);
    this.modal = this.require("#modal", HTMLElement);
    this.stage = this.require("#stage-label", HTMLElement);
    this.progress = this.require("#progress-label", HTMLElement);
    this.time = this.require("#time-label", HTMLElement);
    this.performance = this.require("#performance-label", HTMLElement);
    this.hint = this.require("#focus-hint", HTMLElement);
    this.toast = this.require("#feedback-toast", HTMLElement);
    this.vignette = this.require(".vignette", HTMLElement);
  }

  setActions(preview: () => void, primary: () => void, continueDecision: () => void, cancel: () => void): void {
    this.root.querySelector("#preview-action")?.addEventListener("click", preview);
    this.root.querySelector("#primary-action")?.addEventListener("click", primary);
    this.root.querySelector("#continue-action")?.addEventListener("click", continueDecision);
    this.root.querySelector("#cancel-action")?.addEventListener("click", cancel);
  }

  showPreflight(xrSupported: boolean, onStart: (request: SceneStartRequest) => void, onRecords: () => void): void {
    const stamp = new Date().toISOString().replaceAll(/[-:.TZ]/g, "").slice(0, 14);
    this.setModal(`
      <div class="modal-card wide"><p class="eyebrow">WebXR 场景听觉训练</p><h1>四场景识别范式</h1>
      <p>每个场景最多进行 5 次判断：移动时播放该场景脚步声，从四个方向的照片中选择最匹配场景。被试可自主最终确认；正确直接进入 3D，错误则该场景失败。</p>
      <div class="identity-grid"><label>匿名被试编号<input id="participant-id" value="anon-${stamp.slice(-6)}" maxlength="40"></label>
      <label>会话编号<input id="session-id" value="session-${stamp}" maxlength="40"></label></div>
      <fieldset class="mode-grid"><legend>运行模式</legend>
      <label><input type="radio" name="scene-mode" value="formal" checked><strong>正式模式</strong><span>4 场景 × 最多 5 次</span></label>
      <label><input type="radio" name="scene-mode" value="dev_fast"><strong>开发模式</strong><span>4 场景 × 1 次，直接最终确认</span></label></fieldset>
      <div class="notice">${xrSupported ? "Quest WebXR 已就绪；进入训练后电脑无需串流。" : "当前使用桌面回退：WASD/方向键移动，鼠标转头。"} 数据仅自动保存在当前浏览器。</div>
      <div class="button-row"><button id="records" type="button">本地会话</button><button id="start" class="primary" type="button">启动音频并阅读说明</button></div></div>`);
    this.root.querySelector("#records")?.addEventListener("click", onRecords);
    this.root.querySelector("#start")?.addEventListener("click", () => {
      const participantId = this.root.querySelector<HTMLInputElement>("#participant-id")?.value.trim() ?? "";
      const sessionId = this.root.querySelector<HTMLInputElement>("#session-id")?.value.trim() ?? "";
      if (!participantId || !sessionId) return this.toastMessage("请填写匿名被试与会话编号。", "wrong");
      const mode = this.root.querySelector<HTMLInputElement>('input[name="scene-mode"]:checked')?.value === "dev_fast" ? "dev_fast" : "formal";
      onStart({ participantId, sessionId, mode });
    });
  }

  showRecovery(record: SceneSessionRecord, resume: () => void, abandon: () => void, download: () => void): void {
    this.setModal(`<div class="modal-card"><p class="eyebrow">检测到未完成会话</p><h2>${escapeHtml(record.sessionId)}</h2>
      <p>被试 ${escapeHtml(record.participantId)} · 已保存 ${record.events.length} 条事件</p>
      <div class="stacked-actions"><button id="resume" class="primary">继续训练</button><button id="download">下载现有日志</button><button id="abandon" class="danger">结束旧会话并新建</button></div></div>`);
    this.root.querySelector("#resume")?.addEventListener("click", resume);
    this.root.querySelector("#abandon")?.addEventListener("click", abandon);
    this.root.querySelector("#download")?.addEventListener("click", download);
  }

  showRecords(records: readonly SceneSessionRecord[], download: (id: string) => void, remove: (id: string) => void, back: () => void): void {
    const rows = records.map((record) => `<tr><td>${escapeHtml(record.sessionId)}</td><td>${record.status}</td><td>${record.events.length}</td><td><button data-download="${escapeHtml(record.sessionId)}">下载</button> <button class="danger" data-remove="${escapeHtml(record.sessionId)}">删除</button></td></tr>`).join("");
    this.setModal(`<div class="modal-card wide"><p class="eyebrow">设备本地数据</p><h2>场景训练会话</h2><div class="table-wrap"><table><thead><tr><th>会话</th><th>状态</th><th>事件</th><th>操作</th></tr></thead><tbody>${rows || '<tr><td colspan="4">暂无记录</td></tr>'}</tbody></table></div><button id="back" class="primary">返回</button></div>`);
    this.root.querySelectorAll<HTMLElement>("[data-download]").forEach((button) => button.addEventListener("click", () => download(button.dataset.download ?? "")));
    this.root.querySelectorAll<HTMLElement>("[data-remove]").forEach((button) => button.addEventListener("click", () => remove(button.dataset.remove ?? "")));
    this.root.querySelector("#back")?.addEventListener("click", back);
  }

  showInstruction(page: number, xrSupported: boolean, back: () => void, next: () => void, practice: () => void, skip: () => void): void {
    const pages = [
      ["自由移动并聆听", "在中央决策区移动时，系统循环播放本次目标场景的脚步声。左右摇杆均可移动，后输入者接管；转向完全跟随头部。"],
      ["注视照片并自主确认", `看向四个方向的场景照片。${xrSupported ? "右扳机或 A" : "E"} 第一次选中、第二次作为最终答案；${xrSupported ? "B" : "Q"} 记录本次选择并进入下一组。`],
      ["最终答案决定结果", `正式阶段不采集信心评分。最终答案正确会直接进入 3D 场景；错误则该场景失败。${xrSupported ? "左扳机或 X" : "C"} 可取消选中。练习关可跳过。`],
    ] as const;
    const content = pages[page] ?? pages[0];
    const finalActions = page === 2
      ? `<button id="practice" type="button">进入练习关</button><button id="skip" class="primary" type="button">跳过练习，开始正式训练</button>`
      : `<button id="next" class="primary" type="button">下一页</button>`;
    this.setModal(`<div class="modal-card instruction-card"><p class="eyebrow">操作说明 ${page + 1} / 3</p><h2>${content[0]}</h2><p>${content[1]}</p><div class="button-row"><button id="back" ${page === 0 ? "disabled" : ""}>上一页</button>${finalActions}</div></div>`);
    this.root.querySelector("#back")?.addEventListener("click", back);
    this.root.querySelector("#next")?.addEventListener("click", next);
    this.root.querySelector("#practice")?.addEventListener("click", practice);
    this.root.querySelector("#skip")?.addEventListener("click", skip);
  }

  showSceneFailure(sceneNumber: number, sceneName: string, responseCount: number, onContinue: () => void): void {
    this.setModal(`<div class="modal-card"><p class="eyebrow">场景 ${sceneNumber} 任务失败</p><h2>${escapeHtml(sceneName)}</h2>
      <p>最终确认答案不匹配。本场景共记录 ${responseCount} 次选择，接下来进入下一个场景。</p>
      <button id="failure-continue" class="primary">继续下一个场景</button></div>`);
    this.root.querySelector("#failure-continue")?.addEventListener("click", onContinue);
  }

  showPause(resume: () => void, end: () => void): void {
    this.setModal(`<div class="modal-card"><p class="eyebrow">训练暂停</p><h2>暂停时间不计入有效时间</h2><div class="stacked-actions"><button id="resume" class="primary">继续</button><button id="end" class="danger">提前结束</button></div></div>`);
    this.root.querySelector("#resume")?.addEventListener("click", resume);
    this.root.querySelector("#end")?.addEventListener("click", end);
  }

  showSummary(results: readonly SceneBlockResult[], elapsedSeconds: number, events: number, download: () => void, restart: () => void): void {
    const rows = results.map((result, index) => {
      const correct = result.responses.filter((response) => response.correct).length;
      const final = result.responses.find((response) => response.final);
      return `<tr><td>${index + 1}. ${escapeHtml(result.sceneName)}</td><td>${result.outcome === "success" ? "成功" : "失败"}</td><td>${correct}/${result.responses.length}</td><td>${final ? final.decisionIndex + 1 : "—"}</td></tr>`;
    }).join("");
    this.setModal(`<div class="modal-card wide summary-card"><p class="eyebrow">训练完成</p><h2>会话总结</h2><p>总有效时间 ${formatSeconds(elapsedSeconds)} · ${events} 条事件</p>
      <div class="table-wrap"><table><thead><tr><th>场景</th><th>结果</th><th>选择正确数</th><th>最终确认序号</th></tr></thead><tbody>${rows}</tbody></table></div>
      <p class="prototype-flag">低多边形程序化视觉：research_eligible = false</p><div class="button-row"><button id="download">下载 JSONL + SHA-256</button><button id="restart" class="primary">新会话</button></div></div>`);
    this.root.querySelector("#download")?.addEventListener("click", download);
    this.root.querySelector("#restart")?.addEventListener("click", restart);
  }

  hideModal(): void { this.modal.classList.remove("visible"); this.modal.innerHTML = ""; }
  updateDecision(sceneIndex: number, sceneCount: number, decisionIndex: number, decisionCount: number, practice: boolean): void {
    this.stage.textContent = practice ? "练习关" : `场景 ${sceneIndex + 1} / ${sceneCount}`;
    this.progress.textContent = practice ? "即时反馈 · 可暂停后退出" : `判断 ${decisionIndex + 1} / ${decisionCount}${decisionIndex + 1 >= decisionCount ? " · 最后一次" : ""}`;
  }
  updateTime(seconds: number): void { this.time.textContent = `有效时间 ${formatSeconds(seconds)}`; }
  updatePerformance(fps: number, tier: number): void { this.performance.textContent = `性能 ${Math.round(fps)} FPS · L${tier}`; this.performance.classList.toggle("performance-low", fps < 72); }
  setFocus(direction: Direction | null, pending: Direction | null, reveal = false): void {
    if (reveal) return void (this.hint.textContent = "你已位于正确 3D 场景中心；可自由探索，完成后按 A / E 继续");
    if (!direction) return void (this.hint.textContent = "移动可听脚步声 · 请注视一张照片");
    this.hint.textContent = pending === direction ? `${DIRECTION_LABELS[direction]}已选中 · A/E最终确认 · B/Q继续 · X/C取消` : `${DIRECTION_LABELS[direction]}已聚焦 · 按 A / E 选择`;
  }
  setVignette(moving: boolean, nearWall: boolean): void { this.vignette.classList.toggle("moving", moving); this.vignette.classList.toggle("near-wall", nearWall); }
  toastMessage(message: string, kind: "correct" | "wrong" | "neutral"): void {
    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
    this.toast.textContent = message; this.toast.className = `feedback-toast visible ${kind}`;
    this.toastTimer = window.setTimeout(() => { this.toast.className = "feedback-toast"; this.toastTimer = null; }, 1500);
  }

  private setModal(markup: string): void { this.modal.innerHTML = markup; this.modal.classList.add("visible"); }
  private require<T extends Element>(selector: string, ctor: { new(): T }): T {
    const element = this.root.querySelector(selector);
    if (!(element instanceof ctor)) throw new Error(`Missing HUD element ${selector}`);
    return element;
  }
}

function escapeHtml(value: string): string { return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;"); }
function formatSeconds(seconds: number): string { const value = Math.max(0, Math.floor(seconds)); return `${Math.floor(value / 60).toString().padStart(2, "0")}:${(value % 60).toString().padStart(2, "0")}`; }
